
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Job, Lead, JobStatus, Expense, Activity, NotificationItem } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Icons } from '../constants';
import { apiUrl, authFetch } from '../api';
import { EmergencyBroadcastWidget } from './EmergencyBroadcastWidget';
import WeatherWidget from './WeatherWidget';
import QuickAddJobModal from './QuickAddJobModal';
import { useFirebase } from '../context/FirebaseContext';
import { db } from '../firebase';
import { collection, addDoc, doc, setDoc } from 'firebase/firestore';
import { JoinArborLinkModal } from './JoinArborLinkModal';
import { PostFeelerJobModal } from './PostFeelerJobModal';
import DayPlanner from './DayPlanner';
import ActivityAlertBox from './ActivityAlertBox';
import FileDrop from './FileDrop';
import { OperationsAlerts } from '../lib/operationsAlerts';
import { notificationMeta } from '../lib/notificationMeta';

const WorkspaceWidget = ({ 
  googleConnected, 
  onTriggerNotification 
}: { 
  googleConnected?: boolean; 
  onTriggerNotification?: (params: { title: string; message: string; type: 'APPLICATION' | 'MISSION' | 'SYSTEM'; meta?: any }) => void;
}) => {
  const [events, setEvents] = useState<any[]>([]);
  const [emails, setEmails] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLivePolling, setIsLivePolling] = useState(true);
  const [secondsUntilNextPoll, setSecondsUntilNextPoll] = useState(15);

  const prevEventsRef = React.useRef<any[]>([]);
  const prevEmailsRef = React.useRef<any[]>([]);
  const isFirstLoadRef = React.useRef({ events: true, emails: true });

  const processEvents = (newEvents: any[]) => {
    if (isFirstLoadRef.current.events) {
      prevEventsRef.current = newEvents;
      isFirstLoadRef.current.events = false;
      return;
    }
    newEvents.forEach(evt => {
      const exists = prevEventsRef.current.some(pe => pe.id === evt.id);
      if (!exists) {
        onTriggerNotification?.({
          title: '📅 New Workspace Event Sync',
          message: `Upcoming Job: "${evt.summary}" scheduled for ${new Date(evt.start?.dateTime || evt.start?.date).toLocaleDateString()}`,
          type: 'SYSTEM'
        });
      }
    });
    prevEventsRef.current = newEvents;
  };

  const processEmails = (newEmails: any[]) => {
    if (isFirstLoadRef.current.emails) {
      prevEmailsRef.current = newEmails;
      isFirstLoadRef.current.emails = false;
      return;
    }
    newEmails.forEach(mail => {
      const exists = prevEmailsRef.current.some(pm => pm.id === mail.id);
      if (!exists) {
        onTriggerNotification?.({
          title: '💌 New Workspace Email Alert',
          message: `From ${mail.from}: "${mail.subject}"`,
          type: 'SYSTEM'
        });
      }
    });
    prevEmailsRef.current = newEmails;
  };

  const fetchWorkspaceData = async (silent = false) => {
    if (!googleConnected) return;
    if (!silent) setLoading(true);
    try {
      const [calRes, mailRes] = await Promise.all([
        authFetch('/api/workspace/calendar'),
        authFetch('/api/workspace/gmail')
      ]);
      const calData = await calRes.json();
      const mailData = await mailRes.json();

      if (calData.success) {
        processEvents(calData.events || []);
        setEvents(calData.events || []);
      }
      if (mailData.success) {
        processEmails(mailData.messages || []);
        setEmails(mailData.messages || []);
      }
    } catch (e) {
      console.error("Workspace fetch error", e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Poll timer countdown and trigger
  useEffect(() => {
    if (!googleConnected) return;

    fetchWorkspaceData();

    let countdownInterval: any = null;
    if (isLivePolling) {
      countdownInterval = setInterval(() => {
        setSecondsUntilNextPoll(prev => {
          if (prev <= 1) {
            fetchWorkspaceData(true);
            return 15;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [googleConnected, isLivePolling]);

  // Simulated Workspaces Events for demonstration
  const handleSimulateWorkspaceUpdate = (type: 'EMAIL' | 'CALENDAR') => {
    if (type === 'EMAIL') {
      const senders = ['Tree Climber Pete', 'Customer Support', 'Oak Wilt Labs 🔬', 'ISA Arborist Board', 'Storm Emergency Crew'];
      const subjects = ['Urgent: Safety harness status', 'Estimate approval requested for Oak canopy', 'Lab report: Soil quality results', 'ISA Certification renewal warning', 'Available for high climbers emergency calls'];
      
      const randomSender = senders[Math.floor(Math.random() * senders.length)];
      const randomSubject = subjects[Math.floor(Math.random() * subjects.length)];
      const newEmail = {
        id: `sim-mail-${Date.now()}`,
        from: `${randomSender} <info@workspace.test>`,
        subject: randomSubject,
        snippet: 'This is a simulated incoming Google Workspace email used to demonstrate real-time notifications.'
      };

      setEmails(prev => {
        const withNew = [newEmail, ...prev];
        processEmails(withNew);
        return withNew;
      });
    } else {
      const titles = ['Urgent: Dead limb pruning', 'Drone mapping flight at Riverside', 'Consultation: Pine Beetle mitigation', 'GreenTree Community Board Meetup', 'Aerial Canopy Health Survey'];
      const randomTitle = titles[Math.floor(Math.random() * titles.length)];
      const newEvent = {
        id: `sim-cal-${Date.now()}`,
        summary: `[Simulated] ${randomTitle}`,
        start: { dateTime: new Date(Date.now() + 3600000 * 2).toISOString() }
      };

      setEvents(prev => {
        const withNew = [newEvent, ...prev];
        processEvents(withNew);
        return withNew;
      });
    }
  };

  if (!googleConnected) return null;

  return (
    <div className="bg-white p-8 rounded-[32px] shadow-sm border border-stone-200 lg:col-span-2">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center space-x-2">
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isLivePolling ? 'bg-[#5FA136]' : 'bg-stone-400'}`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${isLivePolling ? 'bg-[#5FA136]' : 'bg-stone-400'}`}></span>
            </span>
            <h4 className="text-[10px] font-black uppercase tracking-widest text-stone-400">Google Workspace Studio</h4>
          </div>
          <h3 className="text-lg font-black text-bark mt-0.5">Real-time Suite Connect</h3>
        </div>

        <div className="flex items-center gap-2">
          {/* Active Poll Status Toggle */}
          <button
            onClick={() => setIsLivePolling(prev => !prev)}
            className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-wider transition ${
              isLivePolling 
                ? 'bg-gecko/10 text-[#5FA136] border border-gecko/20' 
                : 'bg-stone-100 text-stone-500 border border-stone-200'
            }`}
          >
            {isLivePolling ? `🟢 Auto Sync (${secondsUntilNextPoll}s)` : '⏸️ Polling Paused'}
          </button>

          <button
            onClick={() => fetchWorkspaceData()}
            disabled={loading}
            className="p-1.5 bg-stone-100 hover:bg-stone-200 transition text-stone-600 rounded-xl disabled:opacity-50"
            title="Force Workspace Refresh"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin text-[#5FA136]' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 15H17.5" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h5 className="text-sm font-bold text-bark flex items-center">
              <span className="text-lg mr-2">📅</span>
              <span>Google Calendar</span>
            </h5>
            <span className="text-[9px] font-bold text-stone-400 uppercase tracking-widest">{events.length} upcoming</span>
          </div>

          <div className="space-y-2 max-h-[160px] overflow-y-auto custom-scrollbar">
            {events.length === 0 && !loading && (
              <p className="text-xs text-stone-500 italic py-4 text-center">No upcoming events.</p>
            )}
            <AnimatePresence>
              {events.map((event, idx) => (
                <motion.div 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  key={event.id || idx} 
                  className="p-3 bg-stone-50 hover:bg-stone-100/70 transition rounded-2xl border border-stone-100/80 flex flex-col justify-between"
                >
                  <p className="text-xs font-black text-stone-900 leading-tight truncate">{event.summary}</p>
                  <p className="text-[9px] font-semibold text-stone-500 mt-1 uppercase">
                    {new Date(event.start?.dateTime || event.start?.date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                  </p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h5 className="text-sm font-bold text-bark flex items-center">
              <span className="text-lg mr-2">✉️</span>
              <span>Gmail Workspace</span>
            </h5>
            <span className="text-[9px] font-bold text-stone-400 uppercase tracking-widest">{emails.length} unread</span>
          </div>

          <div className="space-y-2 max-h-[160px] overflow-y-auto custom-scrollbar">
            {emails.length === 0 && !loading && (
              <p className="text-xs text-stone-500 italic py-4 text-center">No unread emails.</p>
            )}
            <AnimatePresence>
              {emails.map((email, idx) => (
                <motion.div 
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  key={email.id || idx} 
                  className="p-3 bg-[#5FA136]/5 hover:bg-[#5FA136]/10 transition rounded-2xl border border-[#5FA136]/10 flex flex-col justify-between"
                >
                  <p className="text-xs font-black text-stone-900 leading-tight truncate">{email.subject}</p>
                  <p className="text-[9px] font-bold text-stone-500 mt-1 flex items-center justify-between">
                    <span className="truncate max-w-[70%]">{email.from}</span>
                    <span className="text-gecko bg-white border border-gecko/10 px-1 py-0.5 rounded text-[8px] font-black uppercase">Unread</span>
                  </p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

interface Props {
  jobs: Job[];
  leads: Lead[];
  expenses?: Expense[];
  activities?: Activity[];
  onAddJob?: (job: Job) => void;
  googleConnected?: boolean;
  onConnectGoogle?: () => void;
  onNavigate?: (tab: string) => void;
  notifications?: NotificationItem[];
  onClearNotifications?: () => void;
  onMarkNotificationRead?: (id: string) => void;
  onSimulateNotification?: (type: 'APPLICATION' | 'MISSION') => void;
  onTriggerNotification?: (params: { title: string; message: string; type: 'APPLICATION' | 'MISSION' | 'SYSTEM'; meta?: any }) => void;
  /** Owner-only planner + site activity state, owned by App so alerts fire on every tab. */
  opsAlerts?: OperationsAlerts;
}

const DashboardView: React.FC<Props> = ({ 
  jobs, 
  leads, 
  expenses = [], 
  activities = [], 
  onAddJob, 
  googleConnected, 
  onConnectGoogle, 
  onNavigate,
  notifications = [],
  onClearNotifications,
  onMarkNotificationRead,
  onSimulateNotification,
  onTriggerNotification,
  opsAlerts
}) => {
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [showJoinArborLinkModal, setShowJoinArborLinkModal] = useState(false);
  const [showFeelerJobModal, setShowFeelerJobModal] = useState(false);

  const stats = [
    { label: 'Total CRM Records', value: leads.length + jobs.length, color: 'bg-white text-palm border border-stone-100', to: 'leads', toLabel: 'CRM & Leads' },
    { label: 'Pending Quotes', value: jobs.filter(j => j.status === JobStatus.ESTIMATING).length, color: 'bg-orange-50 text-orange-600 border border-orange-100', to: 'jobs', toLabel: 'Operations' },
    { label: 'Field Operations', value: jobs.filter(j => j.status === JobStatus.SCHEDULED || j.status === JobStatus.IN_PROGRESS).length, color: 'bg-blue-50 text-blue-600 border border-blue-100', to: 'schedule', toLabel: 'Schedule' },
    { label: 'Revenue (MTD)', value: `$${jobs.reduce((acc, j) => acc + (j.status === JobStatus.PAID ? (Number(j.totalPrice) || 0) : 0), 0).toLocaleString()}`, color: 'bg-gecko text-white border-none', to: 'finance', toLabel: 'Financials' },
  ];

  const chartData = [
    { name: 'Mon', jobs: 4 },
    { name: 'Tue', jobs: 3 },
    { name: 'Wed', jobs: 6 },
    { name: 'Thu', jobs: 8 },
    { name: 'Fri', jobs: 5 },
  ];

  const containerVariants: any = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants: any = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
  };

  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="space-y-6 pb-12"
    >
      {/* TOP ROW: Stats & Weather */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <motion.div 
            key={stat.label} 
            variants={itemVariants}
            onClick={() => stat.to && onNavigate?.(stat.to)}
            onKeyDown={(e: any) => {
              if (stat.to && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onNavigate?.(stat.to);
              }
            }}
            role={stat.to ? 'button' : undefined}
            tabIndex={stat.to ? 0 : undefined}
            title={stat.to ? `Open ${stat.toLabel}` : undefined}
            className={`${stat.color} p-6 rounded-3xl shadow-sm flex flex-col justify-center hover:shadow-md transition-all ${stat.to ? 'cursor-pointer hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-palm/40' : ''}`}
          >
            <p className="text-[10px] font-black uppercase tracking-[0.1em] opacity-80 mb-1">{stat.label}</p>
            <h3 className="text-3xl font-black">{stat.value}</h3>
            {stat.to && (
              <span className="mt-2 text-[10px] font-black uppercase tracking-widest opacity-60">
                {stat.toLabel} →
              </span>
            )}
          </motion.div>
        ))}
      </div>

      {/* OWNER DAY PLANNER + SITE ACTIVITY ALERTS */}
      {opsAlerts && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
          <motion.div variants={itemVariants} className="xl:col-span-2">
            <DayPlanner jobs={jobs} alerts={opsAlerts} onNavigate={onNavigate} />
          </motion.div>
          <motion.div variants={itemVariants} className="space-y-6">
            <ActivityAlertBox alerts={opsAlerts} onNavigate={onNavigate} />
            <FileDrop compact title="Quick file drop" />
          </motion.div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* WEATHER WIDGET - live, click for the full forecast */}
        <motion.div variants={itemVariants}>
          <WeatherWidget />
        </motion.div>

        {/* MASCOT WIDGET */}
        <motion.div variants={itemVariants} className="bg-gradient-to-br from-leaf/30 to-palm/20 p-8 rounded-[40px] border-2 border-gold/30 shadow-lg flex flex-col items-center justify-center text-center relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />
          <img 
            src="/greentree-3d.jpg" 
            alt="Arb-E Mascot" 
            className="w-36 h-36 object-cover rounded-3xl mb-4 drop-shadow-xl transform group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.currentTarget.src = "https://api.dicebear.com/7.x/bottts/svg?seed=Arb-E&backgroundColor=A8D672";
            }}
          />
          <h4 className="text-2xl font-black text-bark leading-tight drop-shadow-sm">Cheeky<br/><span className="text-palm text-sm uppercase tracking-widest font-black">Team Mascot</span></h4>
          <p className="text-xs font-bold text-bark/70 mt-2 italic">"Professional Tree Care"</p>
        </motion.div>

        {/* RECENT ACTIVITIES (NEW WIDGET) */}
        <motion.div variants={itemVariants} className="lg:col-span-2 bg-bark p-8 rounded-[32px] text-white shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10"><Icons.Tree /></div>
          <h4 className="text-[10px] font-black text-gecko uppercase tracking-widest mb-6">Team Activity Log</h4>
          <div className="space-y-4 max-h-[220px] overflow-y-auto pr-4 custom-scrollbar">
            {activities.map(activity => (
              <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                key={activity.id} 
                className="flex items-start space-x-4 group"
              >
                <div className={`w-2 h-2 mt-2 rounded-full ${
                  activity.type === 'JOB' ? 'bg-gecko' : 
                  activity.type === 'FINANCE' ? 'bg-orange-400' : 
                  activity.type === 'DRONE' ? 'bg-blue-400' : 'bg-stone-400'
                }`} />
                <div className="flex-1">
                  <p className="text-sm font-medium leading-tight group-hover:text-gecko transition-colors">{activity.message}</p>
                  <p className="text-[10px] text-stone-500 mt-1 font-bold">
                    {new Date(activity.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} • BY {activity.user.toUpperCase()}
                  </p>
                </div>
              </motion.div>
            ))}
            {activities.length === 0 && <p className="text-stone-500 italic text-sm">Waiting for incoming data...</p>}
          </div>
        </motion.div>
      </div>

      {/* ARBORLINK DISCOVERY WIDGET */}
      <motion.div variants={itemVariants} className="bg-gradient-to-r from-gecko/20 via-leaf/20 to-palm/20 p-8 rounded-[32px] border border-gecko/30 shadow-sm relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="absolute top-0 right-0 p-4 opacity-10 transform translate-x-4 -translate-y-4 scale-150 text-gecko"><Icons.Tree /></div>
        <div className="relative z-10 flex-1">
          <h4 className="text-2xl font-black text-bark leading-tight mb-2">Connect & Collaborate</h4>
          <p className="text-sm font-medium text-bark/70 max-w-lg mb-6">Join the ArborLink Network of Tree Care Professionals. Or post "feeler" opportunities to gauge resource availability securely, without client commitments.</p>
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={() => setShowJoinArborLinkModal(true)}
              className="px-6 py-3 bg-bark text-white rounded-2xl font-black tracking-widest uppercase text-xs hover:bg-stone-800 transition shadow-md"
            >
              Join the Network
            </button>
            <button 
              onClick={() => setShowFeelerJobModal(true)}
              className="hidden px-6 py-3 bg-white text-gecko rounded-2xl font-black tracking-widest uppercase text-xs hover:bg-stone-50 border border-gecko/50 transition shadow-sm"
            >
              Post a Feeler Job
            </button>
          </div>
        </div>
        <div className="hidden lg:block relative z-10">
          <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center border-4 border-gecko/20 shadow-xl overflow-hidden">
            <img src="/icon-512x512.png" alt="Arb-E" className="w-24 h-24 object-contain" />
          </div>
        </div>
      </motion.div>

      {/* GOOGLE INTEGRATION WIDGET */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div variants={itemVariants} className="bg-white p-8 rounded-[32px] shadow-sm border border-stone-200 flex flex-col justify-between">
          <div>
            <h4 className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-4">Google Services Integration</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-red-500 shadow-sm">
                    <Icons.Finance />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-bark">Gmail API</p>
                    <p className="text-[10px] text-stone-400 font-bold uppercase">GreenTreeArboriculture.com</p>
                  </div>
                </div>
                <div className={`w-2 h-2 rounded-full ${googleConnected ? 'bg-gecko shadow-[0_0_8px_rgba(95,161,54,0.5)]' : 'bg-stone-300'}`} />
              </div>
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-blue-500 shadow-sm">
                    <Icons.Dashboard />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-bark">Maps Platform</p>
                    <p className="text-[10px] text-stone-400 font-bold uppercase">Geocoding & Static Maps</p>
                  </div>
                </div>
                <div className="w-2 h-2 rounded-full bg-gecko shadow-[0_0_8px_rgba(95,161,54,0.5)]" />
              </div>
            </div>
          </div>
          {googleConnected && (
            <div className="mt-6 p-3 bg-leaf/10 rounded-xl text-center">
              <p className="text-[10px] font-black text-palm uppercase tracking-widest">CONNECTED</p>
            </div>
          )}
        </motion.div>

        {googleConnected && <WorkspaceWidget googleConnected={googleConnected} onTriggerNotification={onTriggerNotification} />}

        {!googleConnected && (
          <motion.div variants={itemVariants} className="bg-white p-8 rounded-[32px] shadow-sm border border-stone-200 lg:col-span-2">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-6">Recent Field Work</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative group rounded-2xl overflow-hidden aspect-square">
                <img src="https://picsum.photos/seed/treecare1/800/800" alt="Field Work 1" className="w-full h-full object-cover group-hover:scale-110 transition duration-500" referrerPolicy="no-referrer" />
                <div className="absolute inset-0 bg-bark/20 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="relative group rounded-2xl overflow-hidden aspect-square">
                <img src="https://picsum.photos/seed/treecare2/800/800" alt="Field Work 2" className="w-full h-full object-cover group-hover:scale-110 transition duration-500" referrerPolicy="no-referrer" />
                <div className="absolute inset-0 bg-bark/20 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="relative group rounded-2xl overflow-hidden aspect-square col-span-2">
                <img src="/helicopter.jpg" alt="Helicopter Operations" className="w-full h-full object-cover group-hover:scale-110 transition duration-500" onError={(e) => {
                  e.currentTarget.src = "https://picsum.photos/seed/treecare3/1600/800";
                }} />
                <div className="absolute inset-0 bg-bark/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white font-black tracking-widest uppercase text-sm">Air Ops</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <motion.div variants={itemVariants} className="bg-white p-8 rounded-[32px] shadow-sm border border-stone-200">
          <EmergencyBroadcastWidget />
          <h4 className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-6 mt-8">Operations Center</h4>
          <div className="grid grid-cols-2 gap-4">
            <button onClick={() => setShowQuickAddModal(true)} className="flex flex-col items-center p-6 bg-stone-50 rounded-[28px] hover:bg-leaf/20 transition group">
              <div className="w-10 h-10 bg-white rounded-2xl shadow-sm mb-3 flex items-center justify-center text-palm group-hover:scale-110 transition"><Icons.Lead /></div>
              <span className="text-[10px] font-black text-stone-600 uppercase text-center">Quick Add<br/>Job</span>
            </button>
            <button onClick={() => onNavigate?.('schedule')} className="flex flex-col items-center p-6 bg-stone-50 rounded-[28px] hover:bg-leaf/20 transition group">
              <div className="w-10 h-10 bg-white rounded-2xl shadow-sm mb-3 flex items-center justify-center text-palm group-hover:scale-110 transition"><Icons.Calendar /></div>
              <span className="text-[10px] font-black text-stone-600 uppercase">Schedule</span>
            </button>
            <button onClick={() => onNavigate?.('finance')} className="flex flex-col items-center p-6 bg-stone-50 rounded-[28px] hover:bg-leaf/20 transition group">
              <div className="w-10 h-10 bg-white rounded-2xl shadow-sm mb-3 flex items-center justify-center text-palm group-hover:scale-110 transition"><Icons.Finance /></div>
              <span className="text-[10px] font-black text-stone-600 uppercase">Billing</span>
            </button>
            <button onClick={() => onNavigate?.('drones')} className="flex flex-col items-center p-6 bg-stone-50 rounded-[28px] hover:bg-leaf/20 transition group">
              <div className="w-10 h-10 bg-white rounded-2xl shadow-sm mb-3 flex items-center justify-center text-palm group-hover:scale-110 transition"><Icons.Drone /></div>
              <span className="text-[10px] font-black text-stone-600 uppercase">Air Ops</span>
            </button>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="lg:col-span-2 bg-white p-8 rounded-[32px] shadow-sm border border-stone-200 overflow-hidden">
          <div className="flex justify-between items-center mb-8">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-stone-400">Pipeline Performance</h4>
            <div className="h-4 w-48 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full bg-gecko w-[65%]" />
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={192}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 900, fill: '#A8A29E'}} />
                <YAxis hide />
                <Tooltip 
                  cursor={{fill: '#f8fafc'}}
                  contentStyle={{borderRadius: '24px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px'}}
                />
                <Bar dataKey="jobs" fill="#5FA136" radius={[12, 12, 12, 12]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Live Push Alert Center & Simulator */}
        <motion.div variants={itemVariants} className="bg-white p-8 rounded-[32px] shadow-sm border border-stone-200 lg:col-span-3">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center space-x-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gecko opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gecko"></span>
                </span>
                <h4 className="text-[10px] font-black uppercase tracking-widest text-stone-400">Arb-E Push Intelligent Notification Services</h4>
              </div>
              <h3 className="text-xl font-black text-bark mt-1">Live Alert Console</h3>
            </div>
            {notifications && notifications.length > 0 && (
              <button 
                onClick={onClearNotifications}
                className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-red-500 bg-red-50 hover:bg-red-100 rounded-xl transition self-start md:self-auto"
              >
                Clear Chimes
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h5 className="text-xs font-black uppercase tracking-widest text-stone-500">Alert Feed of the Day</h5>
                <span className="text-[10px] font-black text-[#5FA136] bg-[#5FA136]/10 px-2.5 py-1 rounded-full uppercase">
                  {notifications.filter(n => !n.read).length} New
                </span>
              </div>

              <div className="bg-stone-50 p-4 rounded-3xl border border-stone-100 max-h-[290px] overflow-y-auto space-y-3 custom-scrollbar animate-fadeIn">
                {notifications.length === 0 && (
                  <div className="py-12 text-center text-stone-400 italic text-sm">
                    <p className="not-italic text-2xl mb-2">🔔</p>
                    <p className="font-bold">No notifications logged yet.</p>
                    <p className="text-[11px] text-stone-400 mt-1 max-w-[240px] mx-auto font-medium">Use the Chime Simulator on the right to manually trigger a simulated cloud event!</p>
                  </div>
                )}
                {notifications.map(noti => (
                  <div 
                    key={noti.id} 
                    onClick={() => onMarkNotificationRead?.(noti.id)}
                    className={`p-4 rounded-2xl border transition cursor-pointer flex gap-3 select-none ${
                      noti.read 
                        ? 'bg-stone-100/50 border-stone-200/60 opacity-60' 
                        : 'bg-white border-gecko/30 shadow-sm hover:border-gecko transition'
                    }`}
                  >
                    <div className="text-xl pt-0.5">
                      {notificationMeta(noti.type).glyph}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className={`text-[9px] font-black uppercase tracking-wider ${
                          notificationMeta(noti.type).text
                        }`}>
                          {notificationMeta(noti.type).glyph}
                        </span>
                        {!noti.read && (
                          <span className="w-1.5 h-1.5 bg-gecko rounded-full flex-shrink-0 animate-pulse" />
                        )}
                      </div>
                      <h4 className="text-xs font-black text-stone-900 truncate">{noti.title}</h4>
                      <p className="text-xs font-semibold text-stone-500 mt-0.5 line-clamp-2">{noti.message}</p>
                      <span className="text-[9px] font-bold text-stone-400 block mt-2">
                        {new Date(noti.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h5 className="text-xs font-black uppercase tracking-widest text-stone-500">Production Cloud Integration</h5>
              <div className="p-6 bg-gradient-to-br from-[#5FA136]/10 to-[#376E20]/10 rounded-3xl border border-[#5FA136]/20 flex flex-col justify-between h-[290px] relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-[0.03] text-palm group-hover:scale-110 transition-transform duration-500">
                  <Icons.Tree className="w-24 h-24" />
                </div>
                <div>
                  <p className="text-xs font-bold text-stone-600 leading-relaxed mb-4">
                    All application views are secured and synchronized in real-time with your Firebase Cloud database backend:
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white/70 rounded-2xl border border-stone-200/60 shadow-sm text-xs">
                      <span className="font-bold text-stone-800 flex items-center gap-2">🔐 Firebase Authentication</span>
                      <span className="text-[#376E20] font-black uppercase text-[10px] tracking-wide">🟢 Enforced</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white/70 rounded-2xl border border-stone-200/60 shadow-sm text-xs">
                      <span className="font-bold text-stone-800 flex items-center gap-2">📂 Firestore Database</span>
                      <span className="text-[#376E20] font-black uppercase text-[10px] tracking-wide">🟢 Real-time Sync</span>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-white/70 p-2.5 rounded-xl border border-stone-200/60 text-center">
                        <p className="text-[10px] font-black text-stone-400">Leads</p>
                        <p className="text-sm font-black text-bark mt-1">{leads.length}</p>
                      </div>
                      <div className="bg-white/70 p-2.5 rounded-xl border border-stone-200/60 text-center">
                        <p className="text-[10px] font-black text-stone-400">Active Jobs</p>
                        <p className="text-sm font-black text-bark mt-1">{jobs.length}</p>
                      </div>
                      <div className="bg-white/70 p-2.5 rounded-xl border border-stone-200/60 text-center">
                        <p className="text-[10px] font-black text-stone-400">Expenses</p>
                        <p className="text-sm font-black text-bark mt-1">{expenses?.length || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-[9px] text-stone-500 font-bold uppercase tracking-wider mt-4 flex items-center justify-between">
                  <span>Security Schema: SHA-256 Enabled</span>
                  <span className="text-[#376E20] font-black">greentree arborist core</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {showQuickAddModal && onAddJob && (
        <QuickAddJobModal
          onClose={() => setShowQuickAddModal(false)}
          onAddJob={(job) => {
            onAddJob(job);
            setShowQuickAddModal(false);
          }}
        />
      )}

      {showJoinArborLinkModal && (
        <JoinArborLinkModal isOpen={showJoinArborLinkModal} onClose={() => setShowJoinArborLinkModal(false)} />
      )}

      {showFeelerJobModal && (
        <PostFeelerJobModal isOpen={showFeelerJobModal} onClose={() => setShowFeelerJobModal(false)} />
      )}
    </motion.div>
  );
};

export default DashboardView;
